import _ from 'lodash';
import { PlantItemRenewalAddInspectionCertificatesPage } from 'plantItemRenewal/constants/plantItemRenewalAddInspectionCertificatesPage';
import { PlantItemRenewalDeclarations } from 'plantItemRenewal/constants/plantItemRenewalDeclarations';
import { PlantItemRenewalReviewItemPage } from 'plantItemRenewal/constants/plantItemRenewalReviewItemPage';
import { InfoCode } from 'shared/constants/InfoCode';
import { PrivacyCollectionNotice } from 'shared/constants/privacyCollectionNotice';
import { formatBlankFields, formatDeclarations, formatSummaryLabels } from 'shared/utils/FormatHelper';
import { capitalizeFirstLetter } from 'shared/utils/Helpers';
import {
  filterRenewalItemsGroupAndSort,
  getInfoCodeValue,
  getItemConditionList,
  getItemType,
  getOtherItemType,
  getStringDate,
  sortItemTypeDescription,
} from './PlantItemRenewalHelpers';

const formatPlantItemRenewalSummary = (state) => {
  const {
    plantItemRenewalApplicantDetails: applicantDetails,
    renewalItems: items,
    referenceData,
    plantItemRenewalDeclaration: declaration,
  } = state;

  const sections = [];
  sections.push(formatApplicantSummary(applicantDetails, items));
  sections.push(...formatRenewalItemsSummary(items.items, referenceData.data));

  formatSummaryLabels(sections);

  const declarations = formatDeclarations(
    PlantItemRenewalDeclarations.consent,
    declaration.stamp,
    applicantDetails.fullName,
    applicantDetails.jobTitle,
  );

  return {
    renewalNumber: items.renewalNumber,
    sections,
    declarations,
    privacyCollection: PrivacyCollectionNotice,
  };
};

const formatApplicantSummary = (applicantDetails, renewalItems) => {
  const subSections = mapAppicantDetailsSubSections(applicantDetails, renewalItems);
  const section = { title: 'Your details and Plant item ownership', subSections: subSections };
  return section;
};

// Applicant Summary
const mapAppicantDetailsSubSections = (applicantDetails, renewalItems) => {
  return [
    { title: 'Registration holder', details: mapHolderDetails(renewalItems) },
    { title: 'Working location', details: mapWorkingLocation(renewalItems) },
    { title: 'Contact details', details: mapContactDetails(applicantDetails) },
  ];
};

const mapHolderDetails = ({ people, businessName }) => {
  if (!people || !people.length) return [];

  const holderDetails = [
    { text: PlantItemRenewalReviewItemPage.ownerLabel, value: people[0]?.holderName },
    { text: 'Business name', value: businessName },
  ];

  return formatBlankFields(holderDetails, true);
};

const mapWorkingLocation = (renewalItems) => {
  return [{ text: PlantItemRenewalReviewItemPage.locationAddressLabel, value: renewalItems.property[0]?.propAddress }];
};

const mapContactDetails = (applicantDetails) => {
  const contactDetails = [
    { text: 'Contact number', value: applicantDetails.phoneNumber.formattedNumber },
    { text: 'Email address', value: applicantDetails.emailAddress },
    { text: 'Ongoing email address', value: applicantDetails.newEmailAddress || applicantDetails.emailAddress },
  ];

  return formatBlankFields(contactDetails);
};

// Renewal Summary
const formatRenewalItemsSummary = (renewalItems, refData) => {
  const sortedItemTypeDescription = sortItemTypeDescription(refData);

  let itemsRenewed = renewalItems.filter((i) => i.isSelected);
  itemsRenewed = filterRenewalItemsGroupAndSort(sortedItemTypeDescription, itemsRenewed);

  let itemsNotRenewed = renewalItems.filter((i) => !i.isSelected);
  itemsNotRenewed = filterRenewalItemsGroupAndSort(sortedItemTypeDescription, itemsNotRenewed);

  const summary = [
    {
      title: 'Plant Item(s) renewed',
      subSections: mapRenewalItemsSubSections(itemsRenewed, refData),
    },
  ];

  if (itemsNotRenewed.length) {
    summary.push({
      title: 'Plant Item(s) not renewed',
      subSections: mapRenewalItemsSubSections(itemsNotRenewed, refData),
    });
  }

  return summary;
};

const mapRenewalItemsSubSections = (groupedItems, refData) => {
  return groupedItems.map((group) => {
    const details = [];

    group.items.forEach((item, index) => {
      details.push(...mapRenewalItemDetails(item));
      const isLastItemInGroup = index + 1 === group.items.length;
      !isLastItemInGroup && details.push({ lineDivider: true });
    });

    return {
      title: group.desc,
      details,
    };
  });
};

const mapRenewalItemDetails = (item) => {
  const labels = PlantItemRenewalReviewItemPage;
  let details = [
    { text: 'Registration number', value: item.tangibleName },
    { text: labels.itemTypeLabel, value: getItemType(item) },
    { text: labels.itemOtherTypeDescLabel, value: getOtherItemType(item) },
    { text: labels.itemSerialNumberLabel, value: item.serialNumber },
    { text: labels.itemSpecificLocationLabel, value: getInfoCodeValue(item, InfoCode.specificLocation) },
    { text: labels.itemUniqueIdLabel, value: getInfoCodeValue(item, InfoCode.customersUid) },
    { text: labels.itemNameOfDeviceLabel, value: getInfoCodeValue(item, InfoCode.amusementDeviceName) },
    { text: labels.itemModelNameOrNumberLabel, value: item.modelNumber },
    { text: labels.itemExpiryDateLabel, value: getStringDate(getInfoCodeValue(item, InfoCode.expiryDate)) },
  ];

  details = formatBlankFields(details, true);

  // Add conditions AFTER formatBlankFields().
  if (getItemConditionList(item)?.length) {
    details.push({ text: labels.itemConditionsLabel, value: '' });
    details.push(...getItemConditionList(item).map((condition) => ({ text: '', value: condition })));
  }

  details.push(...mapElectricalCertificates(item.electricalCertificates));
  details.push(...mapEngineerCertificates(item.engineerCertificates));

  return details;
};

const mapElectricalCertificates = (cert) => {
  const labels = PlantItemRenewalAddInspectionCertificatesPage.electricalInspection;

  if (!cert || _.isEmpty(cert)) return [];

  return [
    { text: '' },
    { text: labels.title },
    { text: labels.competentPersonLabel, value: cert.competentPerson },
    { text: labels.inspectionDateLabel, value: cert.inspectionDate },
    { text: labels.anySpecialConditionsLabel, value: capitalizeFirstLetter(cert.anySpecialConditions) },
    { text: labels.describeConditionsLabel, value: cert.specialConditions },
    ...cert.uploadedFiles.map((f) => ({ text: labels.uploadedFileLabel, value: f.name })),
  ];
};

const mapEngineerCertificates = (cert) => {
  const labels = PlantItemRenewalAddInspectionCertificatesPage.mechanicalInspection;

  if (!cert || _.isEmpty(cert)) return [];

  return [
    { text: '' },
    { text: labels.title },
    { text: labels.competentPersonLabel, value: cert.competentPerson },
    { text: labels.inspectionDateLabel, value: cert.inspectionDate },
    { text: labels.anySpecialConditionsLabel, value: cert.anySpecialConditions },
    { text: labels.describeConditionsLabel, value: cert.specialConditions },
    ...cert.uploadedFiles.map((f) => ({ text: labels.uploadedFileLabel, value: `${f.name} (${f.size})` })),
  ];
};

export { formatPlantItemRenewalSummary };
